import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import bgImage from '../images/background.png';

function SolIcon({ name, size = 20 }) {
    const p = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round', strokeLinejoin: 'round' };
    switch (name) {
        case 'back':   return <svg {...p}><path d="M19 12H5M12 19l-7-7 7-7"/></svg>;
        case 'inbox':  return <svg {...p}><polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/></svg>;
        case 'person': return <svg {...p}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
        case 'check':  return <svg {...p}><path d="M20 6 9 17l-5-5"/></svg>;
        case 'x':      return <svg {...p}><path d="M18 6 6 18M6 6l12 12"/></svg>;
        case 'star':   return <svg {...p}><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>;
        default: return null;
    }
}

export default function Solicitudes() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams();
    const [profileInfo, setProfileInfo] = useState({ id: 0 });
    const [gangName, setGangName]       = useState('');
    const [requests, setRequests]       = useState([]);
    const [ready, setReady]             = useState(false);
    const [acting, setActing]           = useState({});
    const [leaving, setLeaving]         = useState({});

    useEffect(() => {
        const stored = localStorage.getItem('userLogged');
        if (stored) setProfileInfo(JSON.parse(stored));
    }, []);

    useEffect(() => {
        if (!profileInfo.id) return;

        const xhrGang = new XMLHttpRequest();
        xhrGang.open('GET', `${process.env.REACT_APP_API_URL}/winegang/${id}`, true);
        xhrGang.onreadystatechange = function () {
            if (this.readyState === 4 && this.status === 200) {
                const data = JSON.parse(this.responseText);
                if (Number(data.admin_user_id) !== Number(profileInfo.id)) { navigate('/'); return; }
                setGangName(data.winegang?.winegang_name || '');
                fetchRequests();
            }
        };
        xhrGang.send();
    }, [profileInfo.id]);

    const fetchRequests = () => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', `${process.env.REACT_APP_API_URL}/findDemand/${id}`, true);
        xhr.onreadystatechange = function () {
            if (this.readyState === 4 && this.status === 200) {
                setRequests(JSON.parse(this.responseText) || []);
                requestAnimationFrame(() => setReady(true));
            }
        };
        xhr.send();
    };

    const dismiss = (userId, action) => {
        if (acting[userId]) return;
        setActing(prev => ({ ...prev, [userId]: action }));

        const url = action === 'accept'
            ? `${process.env.REACT_APP_API_URL}/confirmDemand/${userId}/${id}`
            : `${process.env.REACT_APP_API_URL}/deleteDemand/${userId}/${id}`;

        const xhr = new XMLHttpRequest();
        xhr.open('GET', url, true);
        xhr.onreadystatechange = function () {
            if (this.readyState === 4 && this.status === 200) {
                setLeaving(prev => ({ ...prev, [userId]: true }));
                setTimeout(() => {
                    setRequests(prev => prev.filter(u => u.id !== userId));
                    setActing(prev => { const n = { ...prev }; delete n[userId]; return n; });
                    setLeaving(prev => { const n = { ...prev }; delete n[userId]; return n; });
                }, 380);
            }
        };
        xhr.send();
    };

    return (
        <div className={`inv-stage${ready ? ' inv-ready' : ''}`}>
            <div className="inv-bg" style={{ backgroundImage: `url(${bgImage})` }} />
            <div className="inv-vignette" />
            <div className="inv-grain" />

            <div className="inv-header">
                <button className="gn-back-btn" onClick={() => navigate(-1)} aria-label={t('common.back')}>
                    <SolIcon name="back" size={18} />
                </button>
                <div className="inv-header-center">
                    <SolIcon name="inbox" size={15} />
                    <span className="inv-header-title">{gangName || t('gangs.requests_title')}</span>
                </div>
                <div className="inv-badge-wrap">
                    {requests.length > 0 && <span className="inv-count-badge">{requests.length}</span>}
                </div>
            </div>

            <div className="inv-scroll">
                {requests.length > 0 ? (
                    <div className="inv-list">
                        {requests.map((user, i) => (
                            <div
                                key={user.id}
                                className={`inv-card${leaving[user.id] ? ' inv-card-leaving' : ''}`}
                                style={{ animationDelay: `${i * 0.07}s` }}
                            >
                                <div className="inv-card-icon">
                                    <SolIcon name="person" size={20} />
                                </div>
                                <div className="inv-card-info">
                                    <span className="inv-gang-name">{user.username}</span>
                                    <div className="inv-gang-meta">
                                        <SolIcon name="star" size={11} />
                                        <span>{user.average.toFixed(1)}% {t('common.average').toLowerCase()}</span>
                                    </div>
                                </div>
                                <div className="inv-card-actions">
                                    <button
                                        className={`inv-btn inv-btn-accept${acting[user.id] === 'accept' ? ' inv-btn-loading' : ''}`}
                                        onClick={() => dismiss(user.id, 'accept')}
                                        disabled={!!acting[user.id]}
                                        aria-label={t('gangs.col_accept')}
                                    >
                                        {acting[user.id] === 'accept' ? <span className="inv-spinner" /> : <SolIcon name="check" size={15} />}
                                    </button>
                                    <button
                                        className={`inv-btn inv-btn-decline${acting[user.id] === 'decline' ? ' inv-btn-loading' : ''}`}
                                        onClick={() => dismiss(user.id, 'decline')}
                                        disabled={!!acting[user.id]}
                                        aria-label={t('gangs.col_delete')}
                                    >
                                        {acting[user.id] === 'decline' ? <span className="inv-spinner" /> : <SolIcon name="x" size={15} />}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : ready ? (
                    <div className="inv-empty">
                        <SolIcon name="inbox" size={36} />
                        <p>{t('gangs.no_requests')}</p>
                    </div>
                ) : null}
            </div>
        </div>
    );
}
